import { Loader, Menu, Text, Tooltip, VisuallyHidden } from "@mantine/core";
import { IconDownload, IconPrinter } from "@tabler/icons-react";
import * as React from "react";
import { DemoExportExcel } from "../../WarehouseInwardNew/pages/DemoExportExcel/DemoExportExcel";
import type { SaleKindConfig } from "../config/saleKinds";
import type { SubmitStatus } from "../types";

interface SaleOutwardToolbarProps {
  kind: SaleKindConfig;
  isSmallScreen: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
  readOnly: boolean;
  locked: boolean;
  hasRows: boolean;
  selectedCount: number;
  onDeleteSelected: () => void;
  submitting: SubmitStatus | null;
  onSubmit: (status: SubmitStatus) => void;
  /** Số vướng mắc còn chặn hoàn thành, hiện trên nút Hoàn thành khi > 0 */
  issueCount: number;
  /** Id phiếu để in; null thì ẩn nút in */
  printableId: number | null;
  onPrint: (id: number) => void;
  showHandover: boolean;
  onDownloadHandover: () => void;
  onPrintHandover: (id: number) => void;
  ecommerceButton: React.ReactNode;
  onPrintPickingList: () => void;
}

type ButtonTone = "blue" | "danger" | "success" | "utility";

const TONE_STYLE: Record<ButtonTone, React.CSSProperties> = {
  blue: { padding: "0 14px", border: "1px solid #C9D6E8", background: "#FFFFFF", color: "#1F5FAE", fontWeight: 600 },
  danger: { padding: "0 12px", border: "1px solid #F2D4D1", background: "#FFFFFF", color: "#B42318", fontWeight: 500 },
  success: { padding: "0 14px", border: 0, background: "#1E6B46", color: "#FFFFFF", fontWeight: 600 },
  utility: { width: 36, padding: 0, border: 0, background: "#1F5FAE", color: "#FFFFFF", justifyContent: "center" },
};

/** Nút xoá bị khoá giữ đúng màu nhạt của thiết kế; các nút khác mờ đi */
const DISABLED_STYLE: Partial<Record<ButtonTone, React.CSSProperties>> = {
  danger: { border: "1px solid #ECD2CF", color: "#B7A5A2" },
};

interface ToolbarButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "style"> {
  tone: ButtonTone;
  icon?: React.ReactNode;
  loading?: boolean;
}

/** Nút cao 36, bo 8 theo thiết kế. forwardRef để làm target cho Menu / Tooltip */
const ToolbarButton = React.forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  ({ tone, icon, loading = false, disabled, children, ...rest }, ref) => {
    const blocked = !!disabled || loading;
    const disabledStyle = disabled && !loading ? DISABLED_STYLE[tone] ?? { opacity: 0.5 } : null;
    return (
      <button
        ref={ref}
        type="button"
        disabled={blocked}
        aria-busy={loading || undefined}
        {...rest}
        style={{
          height: 36,
          boxSizing: "border-box",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          borderRadius: 8,
          font: "inherit",
          fontSize: 13,
          whiteSpace: "nowrap",
          flexShrink: 0,
          cursor: blocked ? "not-allowed" : "pointer",
          ...TONE_STYLE[tone],
          ...disabledStyle,
          ...(loading ? { cursor: "progress" } : null),
        }}
      >
        {loading ? <Loader size={15} color="currentColor" /> : icon}
        {children}
      </button>
    );
  },
);
ToolbarButton.displayName = "ToolbarButton";

const SaveIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 3h11l3 3v15H5z" />
    <path d="M8 3v6h8V3" />
    <path d="M8 21v-7h8v7" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12l5 5L20 7" />
  </svg>
);

const MenuIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M4 7h16M4 12h16M4 17h16" />
  </svg>
);

/**
 * Khoác kiểu thiết kế lên nút của component cũ (DemoExportExcel, PrintTheTMDT) mà không sửa chúng:
 * ghi đè thuộc tính của Button Mantine bên trong bằng selector có độ ưu tiên cao hơn class gốc.
 */
const LEGACY_BUTTON_CSS = `
.so-legacy-btn .mantine-Button-root {
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  background: #FFFFFF;
  color: #1B1F24;
  border: 1px solid #D5D9D2;
}
.so-legacy-btn .mantine-Button-root:hover { background: #F6F7F4; color: #1B1F24; }
.so-legacy-btn--plain .mantine-Button-section { display: none; }
.so-legacy-btn--blue .mantine-Button-root {
  padding: 0 14px;
  font-weight: 600;
  color: #1F5FAE;
  border-color: #C9D6E8;
}
.so-legacy-btn--blue .mantine-Button-root:hover { background: #F2F7FD; color: #1F5FAE; }
`;

const SaleOutwardToolbar = ({
  kind,
  isSmallScreen,
  collapsed,
  onToggleCollapse,
  readOnly,
  locked,
  hasRows,
  selectedCount,
  onDeleteSelected,
  submitting,
  onSubmit,
  issueCount,
  printableId,
  onPrint,
  showHandover,
  onDownloadHandover,
  onPrintHandover,
  ecommerceButton,
  onPrintPickingList,
}: SaleOutwardToolbarProps) => {
  const editDisabled = readOnly || locked;

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: 12 }}>
      <style>{LEGACY_BUTTON_CSS}</style>
      {!isSmallScreen && (
        <Tooltip label="Ctrl + Shift + Z" position="bottom">
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#4A525C",
              fontSize: 12,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            <input
              type="checkbox"
              checked={!collapsed}
              onChange={onToggleCollapse}
              style={{ width: 16, height: 16, margin: 0, accentColor: "#1E6B46", cursor: "pointer" }}
            />
            Thông tin chung
          </label>
        </Tooltip>
      )}

      <div className="so-legacy-btn so-legacy-btn--plain">
        <DemoExportExcel onLySerial={true} noSerialStatus={true} />
      </div>

      <ToolbarButton tone="danger" onClick={onDeleteSelected} disabled={editDisabled || selectedCount === 0}>
        Xóa đã chọn ({selectedCount})
      </ToolbarButton>

      {showHandover && printableId !== null && (
        <>
          <ToolbarButton tone="blue" icon={<IconDownload size={15} />} onClick={onDownloadHandover}>
            Tải biên bản bàn giao
          </ToolbarButton>
          <ToolbarButton tone="blue" icon={<IconPrinter size={15} />} onClick={() => onPrintHandover(printableId)}>
            In biên bản bàn giao
          </ToolbarButton>
        </>
      )}

      {locked && printableId !== null && (
        <ToolbarButton tone="blue" icon={<IconPrinter size={15} />} onClick={() => onPrint(printableId)}>
          {kind.printLabel}
        </ToolbarButton>
      )}
      {locked && ecommerceButton && <div className="so-legacy-btn so-legacy-btn--blue">{ecommerceButton}</div>}

      <ToolbarButton
        tone="blue"
        icon={<SaveIcon />}
        loading={submitting === "OPEN"}
        disabled={editDisabled || !hasRows || (submitting !== null && submitting !== "OPEN")}
        onClick={() => onSubmit("OPEN")}
      >
        Lưu (Ctrl+S)
      </ToolbarButton>
      <ToolbarButton
        tone="success"
        icon={<CheckIcon />}
        loading={submitting === "LOCK"}
        disabled={editDisabled || !hasRows || (submitting !== null && submitting !== "LOCK")}
        onClick={() => onSubmit("LOCK")}
      >
        Hoàn thành (Ctrl+Q)
        {issueCount > 0 && (
          <span
            style={{
              padding: "1px 7px",
              borderRadius: 999,
              background: "#FFFFFF",
              color: "#8A4B05",
              fontSize: 11,
              fontWeight: 700,
              lineHeight: "16px",
            }}
          >
            {issueCount}
            <VisuallyHidden> vướng mắc</VisuallyHidden>
          </span>
        )}
      </ToolbarButton>

      {kind.pickingListPrint && (
        <Menu shadow="md" width={250}>
          <Menu.Target>
            <Tooltip label="Nút tiện ích">
              <ToolbarButton tone="utility" aria-label="Nút tiện ích">
                <MenuIcon />
              </ToolbarButton>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<IconPrinter size={20} />} closeMenuOnClick={false} onClick={onPrintPickingList}>
              <Text size="sm" c="#000" fw={600}>
                In những sản phẩm cần để đi nhặt sản phẩm
              </Text>
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      )}
    </div>
  );
};

export default SaleOutwardToolbar;
